// src/pages/HR/reports/PayrollSummary.js
import React, { useEffect, useMemo, useState } from "react";
import HRCampusSelector from "../../../components/HRCampusSelector";

function payrollLsKey(campus) {
  return `payroll_${String(campus || "gen").toLowerCase()}`;
}
function loadLocalPayroll(campus) {
  try {
    const raw = localStorage.getItem(payrollLsKey(campus));
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}
function exportCSV(filename, rows) {
  const header = ["Employee","Basic Pay","Overtime","Substitution","Total Deduction","Net Pay","Pay Period","Campus"];
  const body = rows.map(r => [
    r.employeeName || "",
    r.basicPay || 0,
    r.overtime || 0,
    r.substitution || 0,
    r.totalDeduction || 0,
    r.amountToBePaid || 0,
    r.payPeriod || "",
    r.campus || ""
  ]);
  const csv = [header, ...body].map(r => r.map(x => `"${String(x).replace(/"/g,'""')}"`).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click(); URL.revokeObjectURL(url);
}

export default function PayrollSummary() {
  const [campus, setCampus] = useState(() => localStorage.getItem("hr_reports_campus") || "bulacan");
  const [period, setPeriod] = useState("");
  const [rows, setRows] = useState([]);
  useEffect(() => { setRows(loadLocalPayroll(campus)); }, [campus]);
  const filtered = useMemo(() => {
    if (!period) return rows;
    return rows.filter(r => String(r.payPeriod || "") === period);
  }, [rows, period]);
  const totals = useMemo(() => {
    return filtered.reduce((acc, r) => {
      acc.basic += Number(r.basicPay || 0);
      acc.overtime += Number(r.overtime || 0);
      acc.sub += Number(r.substitution || 0);
      acc.ded += Number(r.totalDeduction || 0);
      acc.net += Number(r.amountToBePaid || 0);
      return acc;
    }, { basic:0, overtime:0, sub:0, ded:0, net:0 });
  }, [filtered]);

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0">Payroll Summary</h3>
        <div className="d-flex align-items-center gap-2 print-hide">
          <HRCampusSelector selectedCampus={campus} onChange={setCampus} />
          <input type="date" className="form-control" value={period} onChange={e => setPeriod(e.target.value)} title="Select period start (e.g., 2026-03-16)" />
          <button className="btn btn-outline-secondary" onClick={() => window.print()}>Print</button>
          <button className="btn btn-primary" onClick={() => exportCSV(`payroll-summary-${campus}.csv`, filtered)}>Export CSV</button>
        </div>
      </div>
      <p className="text-muted">Monthly payroll breakdown with totals.</p>
      <div className="table-responsive">
        <table className="table table-striped table-bordered">
          <thead className="table-light">
            <tr>
              <th>Employee</th>
              <th>Basic Pay</th>
              <th>Overtime</th>
              <th>Substitution</th>
              <th>Total Deduction</th>
              <th>Net Pay</th>
              <th>Pay Period</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan="7" className="text-center text-muted">No payroll records</td></tr>
            ) : filtered.map((r, i) => (
              <tr key={i}>
                <td>{r.employeeName}</td>
                <td>₱{Number(r.basicPay || 0).toLocaleString()}</td>
                <td>₱{Number(r.overtime || 0).toLocaleString()}</td>
                <td>₱{Number(r.substitution || 0).toLocaleString()}</td>
                <td>₱{Number(r.totalDeduction || 0).toLocaleString()}</td>
                <td>₱{Number(r.amountToBePaid || 0).toLocaleString()}</td>
                <td>{r.payPeriod || ""}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="fw-bold">
              <td className="text-end">Totals</td>
              <td>₱{totals.basic.toLocaleString()}</td>
              <td>₱{totals.overtime.toLocaleString()}</td>
              <td>₱{totals.sub.toLocaleString()}</td>
              <td>₱{totals.ded.toLocaleString()}</td>
              <td>₱{totals.net.toLocaleString()}</td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
